import React, { useMemo } from 'react'
import { compose } from 'redux'
import { lpForm } from 'lp-form'
import { Field, propTypes as formPropTypes } from 'redux-form'
import { CheckboxGroup, ComboBox, ToggleCheckbox } from 'components'
import {
  ButtonArea,
  SubmitButton,
  Input,
  Select,
  Textarea,
} from 'lp-components'
import { serializeOptions, formatObjectsToIds, parseIdsToObjects } from 'utils'
import * as Types from 'types'
import PropTypes from 'prop-types'
import { orderBy, isEmpty } from 'lodash'

// Matches the enumerize values in ApplicationQuestion#type
const FIELD_TYPES = {
  TEXT: 'text',
  TEXTAREA: 'textarea',
  SINGLE_SELECT: 'single_select',
  MULTI_SELECT: 'multi_select',
  BOOLEAN: 'boolean',
}

// Matches ApplicationQuestion#answerable_type
const ANSWERABLE_TYPES = {
  SCHOOL: 'school',
  GRADE: 'grade',
  CA_COMMON_CORE: 'ca_common_core',
  BOOLEAN: 'boolean',
  ORGANIZATION: 'organization',
}

// Static options for questions that have their own defined list (no answerable_type table)
const PROGRAMMING_SETTINGS_OPTIONS = [
  { key: 'In School Programs', value: 'in_school' },
  { key: 'Out of School Programs', value: 'out_of_school' },
  { key: 'None', value: 'none' },
]

const STATIC_OPTIONS_MAP = {
  programming_settings: PROGRAMMING_SETTINGS_OPTIONS,
}

function questionAccessibilityIds(questionId) {
  const labelId = `application-question-${questionId}`
  return {
    labelId,
    controlId: `${labelId}-control`,
    hintId: `${labelId}-hint`,
  }
}

function QuestionLabelBlock({
  label,
  required,
  subDescription,
  htmlFor,
  labelId,
}) {
  const hintId = subDescription && labelId ? `${labelId}-hint` : undefined
  return (
    <>
      <label htmlFor={htmlFor || undefined} id={labelId}>
        {label}
        {required ? (
          <span className="application-field-required" aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </label>
      {subDescription ? (
        <span className="application-field-hint" id={hintId}>
          {subDescription}
        </span>
      ) : null}
    </>
  )
}

QuestionLabelBlock.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  subDescription: PropTypes.string,
  htmlFor: PropTypes.string,
  labelId: PropTypes.string.isRequired,
}

function buildConstraints(questions) {
  const constraints = {}
  if (!questions) return constraints

  questions.forEach((q) => {
    if (!q.required) return
    const { variable, type, answerableType } = q
    if (
      answerableType === ANSWERABLE_TYPES.BOOLEAN ||
      type === FIELD_TYPES.BOOLEAN
    ) {
      constraints[variable] = {
        presence: {
          message: '^This field is required',
        },
      }
      return
    }
    if (type === FIELD_TYPES.MULTI_SELECT) {
      constraints[variable] = {
        presence: {
          message: '^Please select at least one option',
        },
        length: { minimum: 1, message: '^Please select at least one option' },
      }
      return
    }
    constraints[variable] = {
      presence: { message: '^This field is required' },
    }
  })
  return constraints
}

const propTypes = {
  ...formPropTypes,
  questions: PropTypes.arrayOf(Types.applicationQuestion),
  grades: PropTypes.array,
  schoolOptions: PropTypes.array,
  organizationOptions: PropTypes.array,
  caCommonCores: PropTypes.array,
  onSubmit: PropTypes.func,
}

function ApplicationFormStep2({
  error,
  handleSubmit,
  submitting,
  questions,
  grades,
  schoolOptions,
  organizationOptions,
  caCommonCores,
  onSubmit,
}) {
  if (!questions || isEmpty(questions)) return null

  const orderedQuestions = orderBy(questions, 'order')

  const getOptions = (variable, answerableType) => {
    if (STATIC_OPTIONS_MAP[variable]) return STATIC_OPTIONS_MAP[variable]
    switch (answerableType) {
      case ANSWERABLE_TYPES.GRADE:
        return serializeOptions(grades || [], {
          key: 'displayName',
          value: 'id',
        })
      case ANSWERABLE_TYPES.CA_COMMON_CORE:
        return serializeOptions(caCommonCores || [], {
          key: 'name',
          value: 'id',
        })
      default:
        return []
    }
  }

  const renderField = (question) => {
    const {
      id,
      variable,
      content,
      type,
      subDescription,
      answerableType,
      required,
    } = question

    const fieldLabel = content
    const { labelId, controlId, hintId } = questionAccessibilityIds(id)
    const hintDescribedBy = subDescription ? hintId : undefined

    // Boolean toggle (Yes / No)
    if (
      answerableType === ANSWERABLE_TYPES.BOOLEAN ||
      type === FIELD_TYPES.BOOLEAN
    ) {
      return (
        <div key={id} className="label-container">
          <QuestionLabelBlock
            label={fieldLabel}
            required={required}
            subDescription={subDescription}
            labelId={labelId}
            htmlFor={controlId}
          />
          <Field
            name={variable}
            component={ToggleCheckbox}
            label={false}
            className="toggle-checkbox"
            id={controlId}
            aria-describedby={hintDescribedBy}
            aria-required={required || undefined}
          />
        </div>
      )
    }

    // Multi-select organizations
    if (
      answerableType === ANSWERABLE_TYPES.ORGANIZATION &&
      type === FIELD_TYPES.MULTI_SELECT
    ) {
      return (
        <div key={id} className="label-container">
          <QuestionLabelBlock
            label={fieldLabel}
            required={required}
            subDescription={subDescription}
            labelId={labelId}
          />
          <Field
            name={variable}
            label={false}
            component={ComboBox}
            options={serializeOptions(organizationOptions || [], {
              key: 'name',
              value: 'id',
            })}
            isMulti
            format={formatObjectsToIds('id')}
            parse={parseIdsToObjects('id')}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            blurInputOnSelect={false}
            aria-labelledby={labelId}
            aria-describedby={hintDescribedBy}
            aria-required={required || undefined}
          />
        </div>
      )
    }

    // Single-select organizations
    if (
      answerableType === ANSWERABLE_TYPES.ORGANIZATION &&
      type === FIELD_TYPES.SINGLE_SELECT
    ) {
      const opts = serializeOptions(organizationOptions || [], {
        key: 'name',
        value: 'id',
      })
      return (
        <div key={id} className="label-container">
          <QuestionLabelBlock
            label={fieldLabel}
            required={required}
            subDescription={subDescription}
            labelId={labelId}
            htmlFor={controlId}
          />
          <Field
            name={variable}
            label={false}
            placeholder="Select an option"
            component={Select}
            options={opts}
            id={controlId}
            disabled={false}
            aria-describedby={hintDescribedBy}
            required={required}
          />
        </div>
      )
    }

    // Multi-select using ComboBox (schools)
    if (
      answerableType === ANSWERABLE_TYPES.SCHOOL &&
      type === FIELD_TYPES.MULTI_SELECT
    ) {
      return (
        <div key={id} className="label-container">
          <QuestionLabelBlock
            label={fieldLabel}
            required={required}
            subDescription={subDescription}
            labelId={labelId}
          />
          <Field
            name={variable}
            label={false}
            component={ComboBox}
            options={serializeOptions(schoolOptions || [], {
              key: 'name',
              value: 'id',
            })}
            isMulti
            format={formatObjectsToIds('id')}
            parse={parseIdsToObjects('id')}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            blurInputOnSelect={false}
            aria-labelledby={labelId}
            aria-describedby={hintDescribedBy}
            aria-required={required || undefined}
          />
        </div>
      )
    }

    switch (type) {
      case FIELD_TYPES.TEXTAREA:
        return (
          <div key={id} className="label-container">
            <QuestionLabelBlock
              label={fieldLabel}
              required={required}
              subDescription={subDescription}
              labelId={labelId}
              htmlFor={controlId}
            />
            <Field
              name={variable}
              label={false}
              component={Textarea}
              rows={4}
              id={controlId}
              aria-describedby={hintDescribedBy}
              required={required}
            />
          </div>
        )

      case FIELD_TYPES.SINGLE_SELECT: {
        const opts = getOptions(variable, answerableType)
        return (
          <div key={id} className="label-container">
            <QuestionLabelBlock
              label={fieldLabel}
              required={required}
              subDescription={subDescription}
              labelId={labelId}
              htmlFor={controlId}
            />
            <Field
              name={variable}
              label={false}
              placeholder="Select an option"
              component={Select}
              options={opts}
              id={controlId}
              disabled={false}
              aria-describedby={hintDescribedBy}
              required={required}
            />
          </div>
        )
      }

      case FIELD_TYPES.MULTI_SELECT: {
        const opts = getOptions(variable, answerableType)
        return (
          <div key={id} className="label-container">
            <QuestionLabelBlock
              label={fieldLabel}
              required={required}
              subDescription={subDescription}
              labelId={labelId}
            />
            <div
              className="floating-checkboxes"
              role="group"
              aria-labelledby={labelId}
              aria-describedby={hintDescribedBy || undefined}
            >
              <Field
                name={variable}
                label={false}
                component={CheckboxGroup}
                format={formatObjectsToIds('id')}
                parse={parseIdsToObjects('id')}
                options={opts}
                aria-required={required || undefined}
              />
            </div>
          </div>
        )
      }

      case FIELD_TYPES.TEXT:
      default:
        return (
          <div key={id} className="label-container">
            <QuestionLabelBlock
              label={fieldLabel}
              required={required}
              subDescription={subDescription}
              labelId={labelId}
              htmlFor={controlId}
            />
            <Field
              name={variable}
              type="text"
              label={false}
              component={Input}
              id={controlId}
              aria-describedby={hintDescribedBy}
              required={required}
            />
          </div>
        )
    }
  }

  return (
    <form
      className="application-form"
      onSubmit={onSubmit ? handleSubmit(onSubmit) : handleSubmit}
      noValidate
    >
      {error && (
        <p className="full-error-message" role="alert">
          {error}
        </p>
      )}

      {orderedQuestions.map(renderField)}

      <ButtonArea>
        <SubmitButton
          className="full-width button-secondary"
          submitting={submitting}
        >
          Next
        </SubmitButton>
      </ButtonArea>
    </form>
  )
}

ApplicationFormStep2.propTypes = propTypes

function ApplicationFormStep2WithConfig(props) {
  const constraints = useMemo(
    () => buildConstraints(props.questions),
    [props.questions]
  )

  const FormWithConstraints = useMemo(
    () =>
      compose(
        lpForm({
          name: 'teaching-artist-application-step2',
          constraints,
          initialValues: {},
        })
      )(ApplicationFormStep2),
    [constraints]
  )

  return <FormWithConstraints {...props} />
}

export default ApplicationFormStep2WithConfig
